import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EventFilters = ({ onFiltersChange, appliedFilters, onClearFilters }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    dateRange: '',
    revenueMin: '',
    revenueMax: '',
    category: ''
  });

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'published', label: 'Published' },
    { value: 'live', label: 'Live' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const dateRangeOptions = [
    { value: '', label: 'All Dates' },
    { value: 'today', label: 'Today' },
    { value: 'this-week', label: 'This Week' },
    { value: 'this-month', label: 'This Month' },
    { value: 'next-month', label: 'Next Month' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    { value: 'conference', label: 'Conference' },
    { value: 'workshop', label: 'Workshop' },
    { value: 'networking', label: 'Networking' },
    { value: 'seminar', label: 'Seminar' },
    { value: 'webinar', label: 'Webinar' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleClearAll = () => {
    const clearedFilters = {
      search: '',
      status: '',
      dateRange: '',
      revenueMin: '',
      revenueMax: '',
      category: ''
    };
    setFilters(clearedFilters);
    onClearFilters();
  };

  const getAppliedFiltersCount = () => {
    return Object.values(filters)?.filter(value => value !== '')?.length;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      {/* Filter Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <Icon name="Filter" size={20} className="text-muted-foreground" />
          <h3 className="text-lg font-semibold text-foreground">Filters</h3>
          {getAppliedFiltersCount() > 0 && (
            <span className="bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full">
              {getAppliedFiltersCount()}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {getAppliedFiltersCount() > 0 && (
            <Button variant="ghost" size="sm" onClick={handleClearAll}>
              <Icon name="X" size={16} className="mr-1" />
              Clear All
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
          </Button>
        </div>
      </div>
      {/* Search Bar - Always Visible */}
      <div className="mb-4">
        <div className="relative">
          <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search events by name, description, or location..."
            value={filters?.search}
            onChange={(e) => handleFilterChange('search', e?.target?.value)}
            className="pl-10"
          />
        </div>
      </div>
      {/* Expandable Filters */}
      {isExpanded && (
        <div className="space-y-4 animate-fade-in">
          {/* First Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select
              label="Status"
              options={statusOptions}
              value={filters?.status}
              onChange={(value) => handleFilterChange('status', value)}
            />
            <Select
              label="Date Range"
              options={dateRangeOptions}
              value={filters?.dateRange}
              onChange={(value) => handleFilterChange('dateRange', value)}
            />
            <Select
              label="Category"
              options={categoryOptions}
              value={filters?.category}
              onChange={(value) => handleFilterChange('category', value)}
            />
          </div>

          {/* Second Row - Revenue Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Minimum Revenue"
              type="number"
              placeholder="0"
              value={filters?.revenueMin}
              onChange={(e) => handleFilterChange('revenueMin', e?.target?.value)}
            />
            <Input
              label="Maximum Revenue"
              type="number"
              placeholder="No limit"
              value={filters?.revenueMax}
              onChange={(e) => handleFilterChange('revenueMax', e?.target?.value)}
            />
          </div>
        </div>
      )}
      {/* Applied Filters Display */}
      {getAppliedFiltersCount() > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex flex-wrap gap-2">
            {filters?.search && (
              <div className="flex items-center bg-muted text-muted-foreground px-3 py-1 rounded-full text-sm">
                <Icon name="Search" size={12} className="mr-1" />
                Search: "{filters?.search}"
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => handleFilterChange('search', '')}
                  className="ml-1 p-0 h-4 w-4"
                >
                  <Icon name="X" size={10} />
                </Button>
              </div>
            )}
            {filters?.status && (
              <div className="flex items-center bg-muted text-muted-foreground px-3 py-1 rounded-full text-sm">
                Status: {statusOptions?.find(opt => opt?.value === filters?.status)?.label}
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => handleFilterChange('status', '')}
                  className="ml-1 p-0 h-4 w-4"
                >
                  <Icon name="X" size={10} />
                </Button>
              </div>
            )}
            {filters?.category && (
              <div className="flex items-center bg-muted text-muted-foreground px-3 py-1 rounded-full text-sm">
                Category: {categoryOptions?.find(opt => opt?.value === filters?.category)?.label}
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => handleFilterChange('category', '')}
                  className="ml-1 p-0 h-4 w-4"
                >
                  <Icon name="X" size={10} />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventFilters;